import { useMemo, useRef, useState } from 'react';
import { type LayoutChangeEvent, ScrollView, View } from 'react-native';
import { Box, HStack, Text } from '../../../components/primitives';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { CalendarEvent } from '../../../types/calendar';
import { useScrollToNow } from '../hooks/useScrollToNow';
import { getViewerTimeZone, type LocalDate, localDateKey } from '../utils/calendarDate';
import {
  DEFAULT_DAY_WINDOW,
  type EventGeometry,
  getEventGeometry,
  getNowLineRatio,
  type TimeWindow,
} from '../utils/calendarDayGeometry';
import { eventTouchesLocalDay } from '../utils/calendarEventBucketing';
import { type EventBlockLayout, getEventBlockLayout } from '../utils/calendarEventLayout';
import {
  buildHourRows,
  formatHourLabel,
  GUTTER_WIDTH,
  getGridHeight,
  HOUR_HEIGHT,
} from '../utils/calendarHourGrid';
import { assignOverlapLanes } from '../utils/calendarOverlap';
import { CalendarDayEventBlock } from './CalendarDayEventBlock';
import { CalendarNowMarker } from './CalendarNowMarker';
import { DraggableEventBlock } from './DraggableEventBlock';

interface PositionedEvent {
  event: CalendarEvent;
  geometry: EventGeometry;
  layout: EventBlockLayout;
}

interface CalendarDayTimelineProps {
  date: LocalDate;
  events: CalendarEvent[];
  /** Defaults to the viewer's own zone; overridable for tests. */
  timeZone?: string;
  /** Injectable for tests; defaults to the real current instant. */
  now?: Date;
  /** The slice of the day to render; the whole day when not given. */
  window?: TimeWindow;
  /** Opens the edit form — the drill-down destination from the month grid. */
  onEventPress?: (event: CalendarEvent) => void;
  /**
   * Desktop drag-to-reschedule. Absent, blocks are plain pressables; on a
   * phone the hook refuses regardless (see useEventDrag).
   */
  onReschedule?: (event: CalendarEvent, span: { startsAt: string; endsAt: string }) => void;
}

export function CalendarDayTimeline({
  date,
  events,
  timeZone = getViewerTimeZone(),
  now = new Date(),
  window = DEFAULT_DAY_WINDOW,
  onEventPress,
  onReschedule,
}: Readonly<CalendarDayTimelineProps>) {
  const dateKey = localDateKey(date);
  const scrollRef = useRef<ScrollView>(null);
  const [viewportHeight, setViewportHeight] = useState(0);
  const hourRows = buildHourRows(window);
  const gridHeight = getGridHeight(window);

  const { timedEvents, allDayEvents } = useMemo(() => {
    const touching = events.filter((event) => eventTouchesLocalDay(event, dateKey, timeZone));
    return {
      timedEvents: touching.filter((event) => !event.allDay),
      allDayEvents: touching.filter((event) => event.allDay),
    };
  }, [events, dateKey, timeZone]);

  const positioned = useMemo(() => {
    const result: PositionedEvent[] = [];
    for (const { event, lane, laneCount } of assignOverlapLanes(timedEvents)) {
      const geometry = getEventGeometry(event.startsAt, event.endsAt, date, timeZone, window);
      if (!geometry) continue;
      result.push({
        event,
        geometry,
        layout: getEventBlockLayout(geometry, lane, laneCount, gridHeight),
      });
    }
    return result;
  }, [timedEvents, date, timeZone, window, gridHeight]);

  const nowRatio = getNowLineRatio(date, timeZone, now, window);

  useScrollToNow({ scrollRef, nowRatio, gridHeight, viewportHeight });

  return (
    <Box testID="calendar_day_timeline" flex={1}>
      {allDayEvents.length > 0 && (
        <Box
          testID="calendar_day_all_day_strip"
          padding="xs"
          borderBottomWidth={1}
          borderColor={colors.border.muted}
        >
          <HStack spacing="xs" wrap>
            {allDayEvents.map((event) => (
              <CalendarDayEventBlock
                key={event.id}
                event={event}
                onPress={onEventPress}
                testIDPrefix="calendar_all_day_event"
              />
            ))}
          </HStack>
        </Box>
      )}

      <ScrollView
        ref={scrollRef}
        showsVerticalScrollIndicator={false}
        onLayout={(event: LayoutChangeEvent) => setViewportHeight(event.nativeEvent.layout.height)}
      >
        <Box style={{ flexDirection: 'row' }}>
          <View style={{ width: GUTTER_WIDTH }}>
            {hourRows.map((hour) => (
              <Box
                key={hour}
                testID={`calendar_day_hour_${hour}`}
                style={{ height: HOUR_HEIGHT }}
                paddingRight="xs"
                alignItems="flex-end"
              >
                <Text variant="caption" color={colors.text.tertiary}>
                  {formatHourLabel(hour)}
                </Text>
              </Box>
            ))}
          </View>

          <Box flex={1} style={{ position: 'relative' }}>
            {/* Hour rows in normal flow — a bottom border per row, not an
                absolutely-positioned filled rail, gives the grid its hairlines
                AND its total height for free (no manual gridHeight math). */}
            <View>
              {hourRows.map((hour) => (
                <View
                  key={hour}
                  style={{
                    height: HOUR_HEIGHT,
                    borderBottomWidth: 1,
                    borderColor: colors.border.muted,
                  }}
                />
              ))}
            </View>

            {/* Transparent overlay matching the rows' combined height —
                box-none so empty space still lets scroll/touch through to
                whatever's beneath, only the event blocks (and the now-marker,
                which ignores touches entirely) actually capture anything. */}
            <View
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: tokens.spacing.xxs,
                right: tokens.spacing.xxs,
              }}
              pointerEvents="box-none"
            >
              {nowRatio !== null && (
                <CalendarNowMarker ratio={nowRatio} testID="calendar_day_now_line" />
              )}

              {positioned.map(({ event, geometry, layout }) => (
                <DraggableEventBlock
                  key={event.id}
                  event={event}
                  date={date}
                  timeZone={timeZone}
                  window={window}
                  gridHeight={gridHeight}
                  layout={layout}
                  clippedStart={geometry.clippedStart}
                  clippedEnd={geometry.clippedEnd}
                  onPress={onEventPress}
                  onReschedule={onReschedule}
                />
              ))}
            </View>
          </Box>
        </Box>
      </ScrollView>
    </Box>
  );
}
